export const mergeColumns = (columns = [], overrides = []) =>
  columns?.map((column) => {
    const override = overrides?.find((item) => item?.field === column?.field);

    return { ...column, ...override };
  });

export const getColumnVisibilityModel = (columns = []) =>
  columns?.reduce((model, column) => {
    if (!column?.visible) {
      return { ...model, [column?.field]: false };
    }

    return model;
  }, {});

export const getSortModel = (order) => (order ? [order] : []);

export const emptyFiltersParam = '_filters';
export const emptyFiltersValue = 'none';

export const getColumnField = ({ column, name }) =>
  column || name?.split('.')?.[0];

export const isFilterFieldVisible = (field, grid) => {
  const { show = true } = field;
  const shouldShow = typeof show === 'function' ? show({ grid }) : show;

  if (!shouldShow) {
    return false;
  }

  const columnField = getColumnField(field);

  if (!columnField) {
    return true;
  }

  const column = grid?.colunas?.find(({ field }) => field === columnField);

  return !!column?.filterable;
};

export const getValueByPath = (source, path) =>
  path?.split('.')?.reduce((value, key) => value?.[key], source);

const setValueByPath = (source, path, value) => {
  const keys = path?.split('.') || [];
  const lastKey = keys?.pop();
  const target = keys?.reduce((current, key) => {
    if (!current[key]) {
      current[key] = {};
    }

    return current[key];
  }, source);

  if (lastKey) {
    target[lastKey] = value;
  }

  return source;
};

export const hasFilterValue = (value) => {
  if (Array.isArray(value)) {
    return !!value.length;
  }

  return value !== undefined && value !== null && value !== '';
};

const getRangeBaseName = (name = '') => name.replace(/\.(i|f)$/, '');

const getRangeLabel = (field) =>
  field?.rangeLabel ||
  field?.label
    ?.replace(/\s+(Inicial|Final)$/i, '')
    ?.replace(/\s+(Início|Fim)$/i, '') ||
  field?.label;

const getColumnOrder = (field, grid) => {
  const columnField = getColumnField(field);
  const index = grid?.colunas?.findIndex(({ field }) => field === columnField);

  return index >= 0 ? index : Number.MAX_SAFE_INTEGER;
};

const sortFieldsByGridColumns = (fields = [], grid) =>
  fields
    ?.map((field, index) => ({ field, index }))
    ?.sort((a, b) => {
      const columnDiff =
        getColumnOrder(a.field, grid) - getColumnOrder(b.field, grid);

      return columnDiff || a.index - b.index;
    })
    ?.map(({ field }) => field);

export const getFilterItems = (fields = [], grid) => {
  const visibleFields = sortFieldsByGridColumns(
    fields?.filter((field) => isFilterFieldVisible(field, grid)),
    grid
  );

  return visibleFields?.reduce((items, field) => {
    const isRangePart = /\.(i|f)$/.test(field?.name);

    if (!isRangePart) {
      return [
        ...items,
        { type: 'field', key: field.name, label: field.label, field },
      ];
    }

    const baseName = getRangeBaseName(field.name);

    if (items?.some((item) => item.key === baseName)) {
      return items;
    }

    const initialField = visibleFields?.find(
      (item) => item.name === `${baseName}.i`
    );
    const finalField = visibleFields?.find(
      (item) => item.name === `${baseName}.f`
    );

    if (!initialField || !finalField) {
      return [
        ...items,
        { type: 'field', key: field.name, label: field.label, field },
      ];
    }

    return [
      ...items,
      {
        type: 'range',
        key: baseName,
        label: getRangeLabel(initialField),
        fields: [initialField, finalField],
      },
    ];
  }, []);
};

export const getInitialFilterItemKey = (items = []) => {
  const initialItem = items?.find((item) => {
    if (item?.type === 'range') {
      return item?.fields?.some((field) => field?.initial);
    }

    return item?.field?.initial;
  });

  return initialItem?.key || items?.[0]?.key || '';
};

const getDefaultValue = (field) => {
  if (Object.prototype.hasOwnProperty.call(field, 'defaultValue')) {
    return field.defaultValue;
  }

  return getEmptyFilterValue(field);
};

export const getEmptyFilterValue = (field) => {
  if (field?.multiple) {
    return [];
  }

  if (
    field?.type === 'select' ||
    field?.type === 'virtual-select' ||
    field?.type === 'date'
  ) {
    return null;
  }

  return '';
};

export const getFilterDefaultValues = (fields = []) =>
  fields?.reduce(
    (values, field) =>
      setValueByPath(values, field.name, getDefaultValue(field)),
    {}
  );

export const getFilterEmptyValues = (fields = []) =>
  fields?.reduce(
    (values, field) =>
      setValueByPath(values, field.name, getEmptyFilterValue(field)),
    {}
  );

const hasSearchFilterParams = (fields = [], searchParams) =>
  fields?.some((field) => searchParams?.has(field.name));

const hasEmptyFiltersParam = (searchParams) =>
  searchParams?.get(emptyFiltersParam) === emptyFiltersValue;

const parseSearchParamValue = (field, value) => {
  if (field?.multiple) {
    return value;
  }

  if (
    field?.type === 'select' ||
    field?.type === 'virtual-select' ||
    field?.type === 'date'
  ) {
    return value || null;
  }

  return value || '';
};

export const getFilterValuesFromSearchParams = (fields = [], searchParams) => {
  const hasFilterParams = hasSearchFilterParams(fields, searchParams);
  const shouldUseDefaults =
    !hasEmptyFiltersParam(searchParams) && !hasFilterParams;
  const values = shouldUseDefaults
    ? getFilterDefaultValues(fields)
    : getFilterEmptyValues(fields);

  fields?.forEach((field) => {
    if (!searchParams?.has(field.name)) {
      return;
    }

    if (field?.multiple) {
      setValueByPath(values, field.name, searchParams.getAll(field.name));
      return;
    }

    setValueByPath(
      values,
      field.name,
      parseSearchParamValue(field, searchParams.get(field.name))
    );
  });

  return values;
};

export const getSearchParamsFromFilterValues = (fields = [], values = {}) => {
  const searchParams = new URLSearchParams();

  fields?.forEach((field) => {
    const value = getValueByPath(values, field.name);

    if (!hasFilterValue(value)) {
      return;
    }

    if (Array.isArray(value)) {
      value?.forEach((item) => searchParams.append(field.name, item));
      return;
    }

    searchParams.set(field.name, value ?? '');
  });

  return searchParams;
};

export const hasDefaultFilterValues = (fields = []) =>
  !!getSearchParamsFromFilterValues(
    fields,
    getFilterDefaultValues(fields)
  ).toString();
